import { useEffect, useState, type CSSProperties } from "react";
import { photoObjectUrl } from "../../crypto/bundleCrypto";
import { useApp } from "../../context/AppContext";

interface EncryptedPhotoProps {
  stem?: string | null;
  alt: string;
  className?: string;
  style?: CSSProperties;
  fit?: "cover" | "contain";
  fallbackLabel?: string;
}

/** Decrypts and displays a family `.enc` photo by stem. */
export function EncryptedPhoto({
  stem,
  alt,
  className = "",
  style,
  fit = "cover",
  fallbackLabel,
}: EncryptedPhotoProps) {
  const { session } = useApp();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const bundleKey = session?.bundle_key_b64 ?? null;
  const sessionToken = session?.session_token ?? null;

  useEffect(() => {
    // Drop the previous face immediately so a slow decrypt can't flash the wrong person.
    setSrc(null);
    setFailed(false);

    if (!session || !bundleKey || !sessionToken || !stem) {
      return;
    }

    let cancelled = false;
    void photoObjectUrl(session, bundleKey, stem)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(null);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // session is read from this render; token/key strings gate reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid reload on session object identity churn
  }, [stem, bundleKey, sessionToken]);

  if (!src || failed) {
    const label = (fallbackLabel ?? alt).trim().slice(0, 2).toUpperCase() || "·";
    return (
      <div
        className={`photo-fallback ${className}`}
        style={style}
        aria-label={alt}
        role="img"
      >
        {label}
      </div>
    );
  }

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      style={{ objectFit: fit, ...style }}
      draggable={false}
    />
  );
}
